import * as React from 'react';
import { useCallback, useContext, useState } from 'react';

import { useQuery } from '@apollo/client';

import { isFreemiumModelEnabled } from 'bundles/enroll/utils/freemiumModelUtils';
import type {
  GetAiGradingEligibilityByCourseQuery,
  GetAiGradingEligibilityByCourseQueryVariables,
} from 'bundles/peer/api/__generated__/getAiGradingEligibilityByCourse';
import type {
  GetFullyAiGradedConfigsByCourseQuery,
  GetFullyAiGradedConfigsByCourseQueryVariables,
} from 'bundles/peer/api/__generated__/getFullyAiGradedConfig';
import getCourseAiGradingEligibilityQuery from 'bundles/peer/api/getAiGradingEligibilityByCourse.graphql';
import getFullyAiGradedConfigQuery from 'bundles/peer/api/getFullyAiGradedConfig.graphql';
import {
  isFullyAiGradingEnabled,
  isPeerReviewAIGradingRolloutAllEnabledPreview,
  isPeerReviewAIGradingRolloutPaidEnabledPreview,
  isPeerReviewFileAIGradingEnabledPreview,
} from 'bundles/peer/utils/epicUtils';

export type CourseAiGradingContextType = {
  getItemAiGradingEligibility: (itemId: string) => boolean;
  getItemFullyAiGradingEligibility: (itemId: string) => boolean;
  isLoading: boolean;
};

// Components using this context are not owned by our team, and may be used
// outside of `<CourseAiGradingProvider>`. To gracefully degrade in such cases,
// our context supports default value.
const CourseAiGradingContext = React.createContext<CourseAiGradingContextType>({
  getItemAiGradingEligibility: () => false,
  getItemFullyAiGradingEligibility: () => false,
  isLoading: false,
});

export function useCourseAiGradingContext() {
  return useContext(CourseAiGradingContext);
}

export type CourseAiGradingProviderProps = {
  courseId: string;
  children: React.ReactNode;
};

const CourseAiGradingProvider = ({ courseId, children }: CourseAiGradingProviderProps) => {
  const fullyAiGradingEnabled = isFullyAiGradingEnabled();
  const aiFileGradingEnabled = isPeerReviewFileAIGradingEnabledPreview();
  const aiEpicRolloutPaidEnabled = isPeerReviewAIGradingRolloutPaidEnabledPreview();
  const aiEpicRolloutAllEnabled = isPeerReviewAIGradingRolloutAllEnabledPreview();

  const [loading, setLoading] = useState(aiEpicRolloutPaidEnabled || aiEpicRolloutAllEnabled);
  const [fullyAiGradingLoading, setFullyAiGradingLoading] = useState(fullyAiGradingEnabled);

  const [itemEligibility, setItemEligibility] = useState<Record<string, boolean>>({});
  const [fullyAiGradingConfig, setFullyAiGradingConfig] = useState<Record<string, boolean>>({});

  const freemiumModelEnabled = isFreemiumModelEnabled(courseId);
  const onCompleted = (data: GetAiGradingEligibilityByCourseQuery) => {
    setLoading(false);
    const itemsMap = data.PeerReviewAi?.getAiGradingEligibilityByCourse.reduce((acc, item) => {
      const isEligibleFullRollout =
        item.isEligible &&
        item.features.includes('PAID_ENTERPRISE_100') &&
        (aiEpicRolloutPaidEnabled || freemiumModelEnabled);
      const isEligibleFullRolloutAll =
        item.isEligible && item.features.includes('ROLLOUT_ALL') && aiEpicRolloutAllEnabled;
      const isEligibleFileUpload =
        item.isEligible && item.features.includes('FILE_UPLOAD') && (aiFileGradingEnabled || freemiumModelEnabled);
      return { ...acc, [item.itemId]: isEligibleFullRollout || isEligibleFullRolloutAll || isEligibleFileUpload };
    }, {});
    setItemEligibility(itemsMap || {});
  };

  useQuery<GetAiGradingEligibilityByCourseQuery, GetAiGradingEligibilityByCourseQueryVariables>(
    getCourseAiGradingEligibilityQuery,
    {
      variables: {
        courseId,
      },
      skip: !aiEpicRolloutPaidEnabled && !aiEpicRolloutAllEnabled && !aiFileGradingEnabled,
      context: { clientName: 'gatewayGql' },
      onCompleted,
      onError: () => setLoading(false),
    }
  );

  useQuery<GetFullyAiGradedConfigsByCourseQuery, GetFullyAiGradedConfigsByCourseQueryVariables>(
    getFullyAiGradedConfigQuery,
    {
      variables: {
        courseId,
      },
      skip: !fullyAiGradingEnabled,
      context: { clientName: 'gatewayGql' },
      onCompleted: (data) => {
        setFullyAiGradingLoading(false);
        const ids = data.PeerReviewAi?.getFullyAiGradedConfigsByCourse.reduce((acc, curVal) => {
          return { ...acc, [curVal.itemId]: true };
        }, {});
        setFullyAiGradingConfig(ids || {});
      },
      onError: () => {
        setFullyAiGradingLoading(false);
      },
    }
  );

  const getItemAiGradingEligibility = useCallback<(...args: $TSFixMe[]) => $TSFixMe>(
    (itemId: string) => itemEligibility[itemId] || false,
    [itemEligibility]
  );

  const getItemFullyAiGradingEligibility = useCallback<(...args: $TSFixMe[]) => $TSFixMe>(
    (itemId: string) => fullyAiGradingConfig[itemId] || false,
    [fullyAiGradingConfig]
  );

  const isLoading = fullyAiGradingLoading || loading;

  return (
    <CourseAiGradingContext.Provider
      value={{
        isLoading,
        getItemAiGradingEligibility,
        getItemFullyAiGradingEligibility,
      }}
    >
      {children}
    </CourseAiGradingContext.Provider>
  );
};

export default CourseAiGradingProvider;
